"use client"

import React, { useState, useEffect, useCallback, useRef } from 'react'
import useProjectStore from "@/store/projectStore"

function VoiceSelectModal({ isOpen, onClose, onConfirm }) {
    const { selectedVoice, setSelectedVoice } = useProjectStore()
    const modalRef = useRef(null)
    const audioRef = useRef(null) // 音频元素引用
    const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null) // 当前播放的音色 ID

    const [allVoices, setAllVoices] = useState([]) // 所有音色列表（未筛选）
    const [voices, setVoices] = useState([]) // 筛选后的音色列表
    const [selectedGender, setSelectedGender] = useState("All") // 选中的性别筛选：Male, Female, All
    const [selectedAge, setSelectedAge] = useState("All") // 选中的年龄筛选：Middle Aged, Young, Old, All
    const [currentPage, setCurrentPage] = useState(1) // 当前页码
    const [totalPages, setTotalPages] = useState(1) // 总页数
    const [isLoading, setIsLoading] = useState(false) // 是否加载中
    const [tempSelectedVoice, setTempSelectedVoice] = useState(selectedVoice) // 临时选中的音色

    // 控制 modal 的打开和关闭
    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.showModal()
        } else if (!isOpen && modalRef.current) {
            modalRef.current.close()
        }
    }, [isOpen])

    // 获取所有音色列表
    const fetchAllVoices = useCallback(async () => {
        setIsLoading(true)
        try {
            // 获取所有数据，使用 all=true 参数
            const response = await fetch(`/api/elevenlabs-voices?all=true`)
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
                console.error('API Error:', response.status, errorData)
                throw new Error(errorData.error || `Failed to fetch voices (${response.status})`)
            }

            const data = await response.json()
            if (data.error) {
                throw new Error(data.error)
            }
            setAllVoices(data.voices || [])
        } catch (error) {
            console.error('Error fetching voices:', error)
            alert(`Failed to load voices: ${error.message}`)
        } finally {
            setIsLoading(false)
        }
    }, [])

    // 根据筛选条件过滤音色
    const filterVoices = useCallback((voicesList, gender, age) => {
        return voicesList.filter(voice => {
            const labels = voice.labels || {}
            const voiceGender = (labels.gender || "").toLowerCase()
            const voiceAge = (labels.age || "").toLowerCase()

            // Gender 筛选
            if (gender !== "All") {
                const genderMap = {
                    "Male": "male",
                    "Female": "female"
                }
                if (voiceGender !== genderMap[gender]) {
                    return false
                }
            }

            // Age 筛选
            if (age !== "All") {
                const ageMap = {
                    "Middle Aged": "middle_aged",
                    "Young": "young",
                    "Old": "old"
                }
                if (voiceAge !== ageMap[age]) {
                    return false
                }
            }

            return true
        })
    }, [])

    // 分页处理
    const paginateVoices = useCallback((voicesList, page, perPage = 20) => {
        const total = voicesList.length
        const totalPages = Math.ceil(total / perPage)
        const startIndex = (page - 1) * perPage
        const endIndex = startIndex + perPage
        const paginated = voicesList.slice(startIndex, endIndex)

        return {
            voices: paginated,
            totalPages: totalPages || 1,
            totalCount: total
        }
    }, [])

    // 当 modal 打开时加载数据和重置临时选择
    useEffect(() => {
        if (isOpen) {
            setTempSelectedVoice(selectedVoice)
            setSelectedGender("All")
            setSelectedAge("All")
            setCurrentPage(1)
            fetchAllVoices()
        } else {
            // 关闭 modal 时停止所有播放
            stopAudio()
        }
    }, [isOpen, selectedVoice, fetchAllVoices])

    // 清理函数：组件卸载时停止播放
    useEffect(() => {
        return () => {
            stopAudio()
        }
    }, [])

    // 当筛选条件或分页变化时更新显示的音色列表
    useEffect(() => {
        if (isOpen && allVoices.length > 0) {
            // 先筛选
            const filtered = filterVoices(allVoices, selectedGender, selectedAge)
            // 再分页
            const { voices: paginatedVoices, totalPages: pages } = paginateVoices(filtered, currentPage)
            setVoices(paginatedVoices)
            setTotalPages(pages)
        }
    }, [isOpen, allVoices, selectedGender, selectedAge, currentPage, filterVoices, paginateVoices])

    // 当筛选条件变化时重置到第一页
    useEffect(() => {
        if (isOpen) {
            setCurrentPage(1)
        }
    }, [selectedGender, selectedAge, isOpen])

    // 处理确认选择
    const handleConfirm = () => {
        setSelectedVoice(tempSelectedVoice)
        onConfirm(tempSelectedVoice)
        if (modalRef.current) {
            modalRef.current.close()
        }
        onClose()
    }

    // 停止音频播放
    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            audioRef.current = null
        }
        setCurrentlyPlayingId(null)
    }

    // 处理音频播放/暂停
    const handlePlayPause = (e, voice) => {
        e.stopPropagation() // 阻止事件冒泡，避免触发音色选择

        const previewUrl = voice.preview_url
        if (!previewUrl) {
            alert('Preview not available for this voice')
            return
        }

        // 如果点击的是当前正在播放的音色，则暂停
        if (currentlyPlayingId === voice.voice_id && audioRef.current) {
            stopAudio()
            return
        }

        // 停止当前播放的音频
        stopAudio()

        // 创建新的音频元素并播放
        const audio = new Audio(previewUrl)
        audioRef.current = audio
        setCurrentlyPlayingId(voice.voice_id)

        audio.onended = () => {
            stopAudio()
        }

        audio.onerror = () => {
            console.error('Error playing audio preview')
            alert('Failed to play preview')
            stopAudio()
        }

        audio.play().catch((error) => {
            console.error('Error playing audio:', error)
            alert('Failed to play preview')
            stopAudio()
        })
    }

    // 处理关闭
    const handleClose = () => {
        stopAudio() // 关闭时停止播放
        if (modalRef.current) {
            modalRef.current.close()
        }
        onClose()
    }

    // 处理分页
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage)
        }
    }

    // 格式化音色信息显示
    const getVoiceDisplayInfo = (voice) => {
        const labels = voice.labels || {}
        const gender = labels.gender || "unknown"
        const language = labels.language || "unknown"
        const accent = labels.accent || ""

        return {
            id: voice.voice_id,
            name: voice.name || "Unknown",
            gender: gender.charAt(0).toUpperCase() + gender.slice(1),
            language: language.toUpperCase(),
            accent: accent ? ` (${accent})` : ""
        }
    }

    // 根据 gender 和 age 生成渐变背景样式
    const getVoiceGradientStyle = (voice) => {
        const labels = voice.labels || {}
        const gender = (labels.gender || "unknown").toLowerCase()
        const age = (labels.age || "middle_aged").toLowerCase()

        // 年龄亮度映射：young (最亮) -> middle_aged (中等) -> old (最深)
        const ageBrightness = {
            "young": { light: 1.0, dark: 0.86 },      // 最亮最淡
            "middle_aged": { light: 0.7, dark: 0.5 }, // 中等
            "old": { light: 0.5, dark: 0.3 }         // 最深
        }

        const brightness = ageBrightness[age] || ageBrightness["middle_aged"]

        // Male: 蓝色系
        // Female: 粉红色系
        let color1, color2

        if (gender === "male") {
            // 蓝色渐变：从浅蓝到深蓝
            color1 = `rgba(59, 130, 246, ${brightness.light})`      // light blue
            color2 = `rgba(37, 99, 235, ${brightness.dark})`       // darker blue
        } else if (gender === "female") {
            // 粉红色渐变：从浅粉到深粉
            color1 = `rgba(236, 72, 153, ${brightness.light})`     // light pink
            color2 = `rgba(219, 39, 119, ${brightness.dark})`      // darker pink
        } else {
            // 默认：灰色渐变
            color1 = `rgba(156, 163, 175, ${brightness.light})`
            color2 = `rgba(107, 114, 128, ${brightness.dark})`
        }

        return {
            background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`
        }
    }

    return (
        <dialog
            ref={modalRef}
            className="modal modal-bottom sm:modal-middle"
            style={{ backgroundColor: "#ffffff10", backdropFilter: "blur(8px)" }}
            onClose={handleClose}
        >
            <div className="modal-box bg-black rounded-xl border border-base-300 shadow-xl shadow-base-300 max-w-3xl md:min-w-4xl max-h-[90vh] flex flex-col">
                <form method="dialog">
                    <button
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                        onClick={handleClose}
                    >
                        ✕
                    </button>
                </form>

                <div className="mb-4">
                    <h3 className="font-bold text-xl mb-4">Select Voice</h3>

                    {/* 当前选中的音色显示 */}
                    {/* {tempSelectedVoice && (
                        <div className="mb-4">
                            <div>
                                <div className="font-semibold">Current selected:</div>
                                <div className="text-sm">
                                    {tempSelectedVoice.name}
                                    {tempSelectedVoice.labels?.gender || "unknown"} -
                                    {tempSelectedVoice.labels?.language || "unknown"}
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* 筛选选项组 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Gender 筛选 */}
                        <div className='flex items-center gap-4'>
                            <div className="text-sm font-medium text-base-content/70">Gender</div>
                            <div role="tablist" className="tabs tabs-box tabs-sm w-fit">
                                <div role="tab" className={`tab px-4 ${selectedGender === "All" ? "tab-active" : ""}`} onClick={() => setSelectedGender("All")} >All</div>
                                <div role="tab" className={`tab px-4 ${selectedGender === "Male" ? "tab-active" : ""}`} onClick={() => setSelectedGender("Male")} >Male</div>
                                <div role="tab" className={`tab px-4 ${selectedGender === "Female" ? "tab-active" : ""}`} onClick={() => setSelectedGender("Female")} >Female</div>
                            </div>
                        </div>

                        {/* Age 筛选 */}
                        <div className='flex items-center gap-4'>
                            <div className="text-sm font-medium text-base-content/70">Age</div>
                            <div className="tabs tabs-box tabs-sm w-fit">
                                <div className={`tab px-4 ${selectedAge === "All" ? "tab-active" : ""}`} onClick={() => setSelectedAge("All")} >All</div>
                                <div className={`tab px-4 ${selectedAge === "Young" ? "tab-active" : ""}`} onClick={() => setSelectedAge("Young")} >Young</div>
                                <div className={`tab px-4 ${selectedAge === "Middle Aged" ? "tab-active" : ""}`} onClick={() => setSelectedAge("Middle Aged")} >Middle Aged</div>
                                <div className={`tab px-4 ${selectedAge === "Old" ? "tab-active" : ""}`} onClick={() => setSelectedAge("Old")} >Old</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 音色列表 */}
                <div className="flex-1 overflow-y-auto mb-4">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-8">
                            <span className="loading loading-spinner loading-lg"></span>
                        </div>
                    ) : voices.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            No voices found
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {voices.map((voice) => {
                                const info = getVoiceDisplayInfo(voice)
                                const isSelected = tempSelectedVoice?.voice_id === voice.voice_id
                                const isPlaying = currentlyPlayingId === voice.voice_id
                                const hasPreview = !!voice.preview_url

                                return (
                                    <div
                                        key={voice.voice_id}
                                        className={`card bg-base-200 border cursor-pointer transition-all ${isSelected
                                            ? 'border-primary ring-2 ring-primary'
                                            : 'border-base-content/20 hover:border-base-content/40'
                                            }`}
                                        onClick={() => setTempSelectedVoice(voice)}
                                    >
                                        <div className="card-body p-4">
                                            <div className="flex items-start gap-4">
                                                {/* 左侧：圆形播放按钮 */}
                                                <div className="flex-shrink-0">
                                                    <button
                                                        className={`btn btn-circle w-12 h-12 min-h-0 ${!hasPreview ? 'btn-disabled opacity-50' : ''} border-0 shadow-md hover:shadow-lg transition-all`}
                                                        style={hasPreview ? {
                                                            ...getVoiceGradientStyle(voice),
                                                            width: '3rem',
                                                            height: '3rem',
                                                            borderRadius: '50%'
                                                        } : {
                                                            width: '3rem',
                                                            height: '3rem',
                                                            borderRadius: '50%'
                                                        }}
                                                        onClick={(e) => handlePlayPause(e, voice)}
                                                        disabled={!hasPreview}
                                                        title={hasPreview ? (isPlaying ? 'Pause preview' : 'Play preview') : 'Preview not available'}
                                                    >
                                                        {isPlaying ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>

                                                {/* 右侧：音色信息 */}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="card-title text-base">
                                                        {info.name}
                                                        {isSelected && (
                                                            <span className="badge badge-primary badge-sm ml-2">Selected</span>
                                                        )}
                                                    </h4>
                                                    <div className="text-sm text-base-content mt-1">
                                                        <span>{info.language} 🇬🇧🇺🇸</span>
                                                        <span className="mx-2">•</span>
                                                        <span>{info.gender}{info.accent}</span>
                                                    </div>
                                                    {voice.description && (
                                                        <p className="text-xs text-base-content/60 mt-2 line-clamp-2">
                                                            {voice.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* 底部操作栏：分页控件和确认按钮 */}
                <div className="flex justify-between items-center gap-4">
                    {/* 分页控件 - 左侧 */}
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoading}
                        >
                            Previous
                        </button>
                        <span className="text-sm text-base-content/70">
                            Page {currentPage} / {totalPages}
                        </span>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoading}
                        >
                            Next
                        </button>
                    </div>

                    {/* 确认按钮 - 右侧 */}
                    <div className="flex gap-2">
                        <button className="btn btn-ghost" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleConfirm}
                            disabled={!tempSelectedVoice}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </dialog>
    )
}

export default VoiceSelectModal

